import { useState, useEffect } from 'react';
import {
  Button,
  Group,
  Select,
  Stack,
  Text,
  Alert,
  Loader,
  Center,
  Card,
  Switch,
  Table,
  Title,
  Box
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconDeviceFloppy } from '@tabler/icons-react';
import classes from './ExportConfigManagement.module.css';
import {
  getAllExportColumns,
  getClientExportConfig,
  updateColumnActiveStatus,
  bulkUpdateVisibility
} from '../../services/ExportConfigService';
import { getAddressSystems } from '../../services/UserRoleService';
import {
  ExportColumn,
  ClientExportConfig,
  ColumnVisibilityUpdate
} from '../../types/ExportConfig';
import { AddressSystem } from '../../types/UserRole';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

export default function ExportConfigManagement() {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 333, addressSystemId: addrIdFromStore || 0, autoLoad: true });

  const [addressSystems, setAddressSystems] = useState<AddressSystem[]>([]);
  const [selectedAddressSystemId, setSelectedAddressSystemId] = useState<string | null>(null);
  
  // Global columns state (when no address system selected)
  const [globalColumns, setGlobalColumns] = useState<ExportColumn[]>([]);
  const [originalGlobalColumns, setOriginalGlobalColumns] = useState<ExportColumn[]>([]);
  
  // Client config state (when address system selected)
  const [clientConfig, setClientConfig] = useState<ClientExportConfig[]>([]);
  const [originalClientConfig, setOriginalClientConfig] = useState<ClientExportConfig[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load address systems on mount
  useEffect(() => {
    const loadAddressSystems = async () => {
      try {
        const systems = await getAddressSystems();
        setAddressSystems(systems);
      } catch (error) {
        notifications.show({
          title: getTranslation("LABEL_EXPORT_CONFIG_ERROR_TITLE") || "Error",
          message: getTranslation("LABEL_EXPORT_CONFIG_LOAD_ADDRESS_SYSTEMS_FAILED") || "Failed to load address systems",
          color: 'red',
          withBorder: true,
          withCloseButton: true,
          autoClose: 5000,
        });
      }
    };

    loadAddressSystems();
  }, []);

  // Load data based on selection
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!selectedAddressSystemId) {
          // Load global columns
          const columns = await getAllExportColumns();
          setGlobalColumns(columns);
          setOriginalGlobalColumns(JSON.parse(JSON.stringify(columns)));
          setClientConfig([]);
          setOriginalClientConfig([]);
        } else {
          // Load client config for selected address system
          const config = await getClientExportConfig(parseInt(selectedAddressSystemId));
          setClientConfig(config);
          setOriginalClientConfig(JSON.parse(JSON.stringify(config)));
          setGlobalColumns([]);
          setOriginalGlobalColumns([]);
        }
      } catch (error) {
        notifications.show({
          title: getTranslation("LABEL_EXPORT_CONFIG_ERROR_TITLE") || "Error",
          message: getTranslation("LABEL_EXPORT_CONFIG_LOAD_FAILED") || "Failed to load export configuration",
          color: 'red',
          withBorder: true,
          withCloseButton: true,
          autoClose: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedAddressSystemId]);

  // Handle toggle for global column active status
  const handleGlobalToggle = (columnId: number, isActive: boolean) => {
    setGlobalColumns(prev =>
      prev.map(col =>
        col.id === columnId ? { ...col, isActive } : col
      )
    );
  };

  // Handle toggle for client config visibility
  const handleClientToggle = (columnId: number, isVisible: boolean) => {
    setClientConfig(prev =>
      prev.map(config =>
        config.columnId === columnId ? { ...config, isVisible } : config
      )
    );
  };

  // Check if there are unsaved changes
  const hasGlobalChanges = () => {
    return JSON.stringify(globalColumns) !== JSON.stringify(originalGlobalColumns);
  };

  const hasClientChanges = () => {
    return JSON.stringify(clientConfig) !== JSON.stringify(originalClientConfig);
  };

  // Save global columns
  const saveGlobalColumns = async () => {
    setSaving(true);
    try {
      // Find columns that changed
      const changedColumns = globalColumns.filter((col, index) => {
        const original = originalGlobalColumns[index];
        return original && col.isActive !== original.isActive;
      });

      // Update each changed column
      for (const col of changedColumns) {
        await updateColumnActiveStatus({
          columnId: col.id,
          isActive: col.isActive
        });
      }

      // Update original state
      setOriginalGlobalColumns(JSON.parse(JSON.stringify(globalColumns)));

      notifications.show({
        title: getTranslation("LABEL_EXPORT_CONFIG_SUCCESS_TITLE") || "Success",
        message: getTranslation("LABEL_EXPORT_CONFIG_GLOBAL_SAVED") || "Global export configuration saved successfully",
        color: 'green',
        withBorder: true,
        withCloseButton: true,
        autoClose: 3000,
      });
    } catch (error) {
      notifications.show({
        title: getTranslation("LABEL_EXPORT_CONFIG_ERROR_TITLE") || "Error",
        message: getTranslation("LABEL_EXPORT_CONFIG_GLOBAL_SAVE_FAILED") || "Failed to save global export configuration",
        color: 'red',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Save client config
  const saveClientConfig = async () => {
    if (!selectedAddressSystemId) return;

    setSaving(true);
    try {
      // Build updates for changed items
      const updates: ColumnVisibilityUpdate[] = clientConfig
        .filter((config, index) => {
          const original = originalClientConfig[index];
          return original && config.isVisible !== original.isVisible;
        })
        .map(config => ({
          columnId: config.columnId,
          isVisible: config.isVisible
        }));

      if (updates.length > 0) {
        await bulkUpdateVisibility({
          addressSystemId: parseInt(selectedAddressSystemId),
          updates
        });
      }

      // Update original state
      setOriginalClientConfig(JSON.parse(JSON.stringify(clientConfig)));

      notifications.show({
        title: getTranslation("LABEL_EXPORT_CONFIG_SUCCESS_TITLE") || "Success",
        message: getTranslation("LABEL_EXPORT_CONFIG_SAVED") || "Export configuration saved successfully",
        color: 'green',
        withBorder: true,
        withCloseButton: true,
        autoClose: 3000,
      });
    } catch (error) {
      notifications.show({
        title: getTranslation("LABEL_EXPORT_CONFIG_ERROR_TITLE") || "Error",
        message: getTranslation("LABEL_EXPORT_CONFIG_SAVE_FAILED") || "Failed to save export configuration",
        color: 'red',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Render global columns table
  const renderGlobalColumnsTable = () => (
    <Table striped highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{getTranslation("LABEL_EXPORT_CONFIG_COLUMN_KEY") || "Column Key"}</Table.Th>
          <Table.Th>{getTranslation("LABEL_EXPORT_CONFIG_DISPLAY_NAME") || "Display Name"}</Table.Th>
          <Table.Th style={{ width: 120, textAlign: 'center' }}>{getTranslation("LABEL_EXPORT_CONFIG_ACTIVE") || "Active"}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {globalColumns.map(col => (
          <Table.Tr key={col.id}>
            <Table.Td>
              <Text size="sm" c="dimmed" ff="monospace">{col.columnKey}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{col.displayName}</Text>
            </Table.Td>
            <Table.Td style={{ textAlign: 'center' }}>
              <Switch
                checked={col.isActive}
                onChange={(e) => handleGlobalToggle(col.id, e.currentTarget.checked)}
                color="green"
              />
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );

  // Render client config table
  const renderClientConfigTable = () => (
    <Table striped highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{getTranslation("LABEL_EXPORT_CONFIG_COLUMN_KEY") || "Column Key"}</Table.Th>
          <Table.Th>{getTranslation("LABEL_EXPORT_CONFIG_DISPLAY_NAME") || "Display Name"}</Table.Th>
          <Table.Th style={{ width: 120, textAlign: 'center' }}>{getTranslation("LABEL_EXPORT_CONFIG_GLOBALLY_ACTIVE") || "Globally Active"}</Table.Th>
          <Table.Th style={{ width: 120, textAlign: 'center' }}>{getTranslation("LABEL_EXPORT_CONFIG_VISIBLE") || "Visible"}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {clientConfig.map(config => (
          <Table.Tr 
            key={config.id}
            style={{ opacity: config.isGloballyActive ? 1 : 0.5 }}
          >
            <Table.Td>
              <Text size="sm" c="dimmed" ff="monospace">{config.columnKey}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{config.displayName}</Text>
            </Table.Td>
            <Table.Td style={{ textAlign: 'center' }}>
              <Switch
                checked={config.isGloballyActive}
                disabled
                color="blue"
              />
            </Table.Td>
            <Table.Td style={{ textAlign: 'center' }}>
              <Switch
                checked={config.isVisible}
                onChange={(e) => handleClientToggle(config.columnId, e.currentTarget.checked)}
                disabled={!config.isGloballyActive}
                color="green"
              />
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );

  return (
    <Box className={classes.container}>
      <Card className={classes.cardContainer}>
        <Stack gap="md" className={classes.stackContainer}>
          <Title order={4}>{getTranslation("LABEL_EXPORT_CONFIG_TITLE") || "Export Configuration"}</Title>

          <Alert color="blue" icon={<IconAlertCircle size={18} />}>
            <Text size="sm">
              {getTranslation("LABEL_EXPORT_CONFIG_DESCRIPTION") || "Configure which columns are available for XLSX exports. Select Global Settings to enable/disable columns for all address systems, or select a specific address system to configure visibility for that system only."}
            </Text>
          </Alert>

          <Select
            label={getTranslation("LABEL_CONFIGURATION_SCOPE") || "Configuration Scope"}
            placeholder={getTranslation("PLACEHOLDER_SELECT_SCOPE") || "Select scope"}
            value={selectedAddressSystemId}
            onChange={setSelectedAddressSystemId}
            data={[
              { value: '', label: getTranslation("LABEL_EXPORT_CONFIG_GLOBAL_SETTINGS") || "Global Settings (All Address Systems)" },
              ...addressSystems.map(sys => ({
                value: sys.id.toString(),
                label: sys.name
              }))
            ]}
            clearable={false}
            allowDeselect={false}
            searchable
          />

          {loading ? (
            <Center py="xl">
              <Loader size="lg" />
            </Center>
          ) : (
            <>
              <Box className={classes.contentSection}>
                {!selectedAddressSystemId ? (
                  <>
                    <Text size="sm" fw={500} c="dimmed">
                      {getTranslation("LABEL_EXPORT_CONFIG_GLOBAL_COLUMNS_HELP") || "Global Column Settings - Toggle columns on/off for all address systems"}
                    </Text>
                    {globalColumns.length > 0 ? (
                      renderGlobalColumnsTable()
                    ) : (
                      <Alert color="yellow">
                        {getTranslation("LABEL_EXPORT_CONFIG_NO_GLOBAL_COLUMNS") || "No export columns found. They will be created automatically when the export feature is first used."}
                      </Alert>
                    )}
                  </>
                ) : (
                  <>
                    <Text size="sm" fw={500} c="dimmed">
                      {getTranslation("LABEL_EXPORT_CONFIG_ADDRESS_SYSTEM_VISIBILITY") || "Address System Visibility - Toggle which columns are visible for exports in this address system"}
                    </Text>
                    {clientConfig.length > 0 ? (
                      renderClientConfigTable()
                    ) : (
                      <Alert color="yellow">
                        {getTranslation("LABEL_EXPORT_CONFIG_NO_ADDRESS_SYSTEM_CONFIG") || "No configuration found for this address system. It will be created automatically when the export feature is first used."}
                      </Alert>
                    )}
                  </>
                )}
              </Box>

              <Group justify="flex-end" mt="md" style={{ flexShrink: 0 }}>
                <Button
                  leftSection={<IconDeviceFloppy size={16} />}
                  loading={saving}
                  disabled={
                    saving ||
                    (!selectedAddressSystemId && !hasGlobalChanges()) ||
                    (!!selectedAddressSystemId && !hasClientChanges())
                  }
                  onClick={!selectedAddressSystemId ? saveGlobalColumns : saveClientConfig}
                >
                  {getTranslation("LABEL_EXPORT_CONFIG_SAVE_CHANGES") || "Save Changes"}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Card>
    </Box>
  );
}
